import { LINK_OR_MARKUP, inviteeProblem, type InviteeField } from '../views/workflow-studio/settings.js';
import type Invitation from '../models/invitation.js';

// What the request card asks of a company it invites by email, checked as company/connect-create checks it

export type InviteeDraft = Record<InviteeField, string> & { registrarIdentifier: string };
export type InviteeDraftField = keyof InviteeDraft;

export const MAX_REQUEST_COMPANIES = 20;
const REGISTRAR_IDENTIFIER = /^[A-Za-z0-9]{1,20}$/;

export const emptyInvitee = (): InviteeDraft => ({
  companyName: '',
  name: '',
  jobTitle: '',
  email: '',
  registrarIdentifier: '',
});

export function inviteeErrors(draft: InviteeDraft): Partial<Record<InviteeDraftField, string>> {
  const errors: Partial<Record<InviteeDraftField, string>> = {};
  for (const field of ['companyName', 'name', 'jobTitle', 'email'] as const) {
    const problem = inviteeProblem(field, draft[field]);
    if (problem) errors[field] = problem;
  }
  const registrarIdentifier = draft.registrarIdentifier.trim();
  if (registrarIdentifier && !REGISTRAR_IDENTIFIER.test(registrarIdentifier)) {
    errors.registrarIdentifier = 'Use letters and digits only.';
  }
  return errors;
}

// The checks across the whole request, once each invitee's own fields are fine
export function requestProblem(pickedCount: number, invitees: InviteeDraft[], notes: string): string | undefined {
  if (pickedCount + invitees.length === 0) return 'Pick a company, or invite one by email.';
  if (pickedCount + invitees.length > MAX_REQUEST_COMPANIES) {
    return `A request can ask at most ${MAX_REQUEST_COMPANIES} companies.`;
  }
  const emails = invitees.map((invitee) => invitee.email.trim().toLowerCase());
  if (new Set(emails).size !== emails.length) return 'Each company invited by email needs its own address.';
  if (invitees.length > 0 && LINK_OR_MARKUP.test(notes)) {
    return 'Your note goes by email to the companies you invite, so leave out links, web addresses and < or >.';
  }
  return undefined;
}

export function inviteePayload(draft: InviteeDraft) {
  const registrarIdentifier = draft.registrarIdentifier.trim();
  return {
    companyName: draft.companyName.trim(),
    name: draft.name.trim(),
    jobTitle: draft.jobTitle.trim(),
    email: draft.email.trim().toLowerCase(),
    ...(registrarIdentifier ? { registrarIdentifier } : {}),
  };
}

// The invitation fields the inviting company's people can read (the user policy's company read)
export type SentInvitation = Pick<Invitation, 'id' | 'status' | 'relationshipId'> & {
  invitee: { name: string | null; email: string | null; company: { name: string | null } };
  expiryDate: string | Date | null;
};

export const isExpired = (invitation: Pick<SentInvitation, 'expiryDate'>, now: Date) =>
  !!invitation.expiryDate && new Date(invitation.expiryDate) <= now;

// Still to join: PENDING (expired or not) can be withdrawn or sent again, IN_PROGRESS is a sign-up under way
export const awaitingInvitations = <T extends SentInvitation>(invitations: T[]) =>
  invitations.filter((invitation) => invitation.status === 'PENDING' || invitation.status === 'IN_PROGRESS');

// An invitation that could still be taken up is an invite sent, as an INVITED party is
export const openInvitationCount = (invitations: SentInvitation[], now: Date) =>
  awaitingInvitations(invitations).filter(
    (invitation) => invitation.status === 'IN_PROGRESS' || !isExpired(invitation, now),
  ).length;

export function invitationState(invitation: SentInvitation, now: Date, formatDate: (date: Date) => string): string {
  if (invitation.status === 'IN_PROGRESS') return 'Signing up';
  if (isExpired(invitation, now)) return 'Invitation expired';
  return invitation.expiryDate ? `Expires ${formatDate(new Date(invitation.expiryDate))}` : 'Invitation sent';
}
